import type { TransformDirection } from '@/api/common/types'
import BigNumber from 'bignumber.js'

export function getEnvCdnBaseUrl() {
  // 请求cdn基准地址
  const baseUrl = import.meta.env.VITE_CDN_BASEURL
  return baseUrl
}
export function getFullURL(url: string): string {
  if (!url || url.trim().length === 0) {
    return ''
  }

  // 1. 统一将反斜杠替换为正斜杠（兼容 Windows 风格路径）
  const normalizedUrl = url.replace(/\\/g, '/')

  // 2. 如果已经是完整 URL，直接返回
  if (normalizedUrl.startsWith('http://') || normalizedUrl.startsWith('https://')) {
    return normalizedUrl
  }

  return `${getEnvCdnBaseUrl()}${normalizedUrl.startsWith('/') ? '' : '/'}${normalizedUrl}`
}

/**
 *
 * @param component 需要注册的组件
 * @param alias 组件别名
 * @returns any
 */
export const withInstall = <T>(component: T, alias?: string) => {
  const comp = component as any
  comp.install = (app: any) => {
    app.component(comp.name || comp.displayName, component)
    if (alias) {
      app.config.globalProperties[alias] = component
    }
  }
  return component as T & Plugin
}

/**
 * @param str 需要转下划线的驼峰字符串
 * @returns 字符串下划线
 */
export const humpToUnderline = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase()
}

/**
 * @param str 需要转驼峰的下划线字符串
 * @returns 字符串驼峰
 */
export const underlineToHump = (str: string): string => {
  if (!str) return ''
  return str.replace(/\-(\w)/g, (_, letter: string) => {
    return letter.toUpperCase()
  })
}

/**
 * 驼峰转横杠
 */
export const humpToDash = (str: string): string => {
  return str.replace(/([A-Z])/g, '-$1').toLowerCase()
}

export const setCssVar = (prop: string, val: any, dom = document.documentElement) => {
  dom.style.setProperty(prop, val)
}

export const getCssVar = (prop: string, dom = document.documentElement) => {
  return getComputedStyle(dom).getPropertyValue(prop)
}

/**
 * 查找数组对象的某个下标
 * @param {Array} ary 查找的数组
 * @param {Functon} fn 判断的方法
 */
export const findIndex = <T = Recordable>(ary: Array<T>, fn: Fn): number => {
  if (ary.findIndex) {
    return ary.findIndex(fn)
  }
  let index = -1
  ary.some((item: T, i: number, ary: Array<T>) => {
    const ret: T = fn(item, i, ary)
    if (ret) {
      index = i
      return ret
    }
  })
  return index
}

export const trim = (str: string) => {
  return str.replace(/(^\s*)|(\s*$)/g, '')
}

/**
 * @param {Date | number | string} time 需要转换的时间
 * @param {String} fmt 需要转换的格式 如 yyyy-MM-dd、yyyy-MM-dd HH:mm:ss
 */
export function formatTime(time: Date | number | string, fmt: string) {
  if (!time) return ''
  else {
    const date = new Date(time)
    const o = {
      'M+': date.getMonth() + 1,
      'd+': date.getDate(),
      'H+': date.getHours(),
      'm+': date.getMinutes(),
      's+': date.getSeconds(),
      'q+': Math.floor((date.getMonth() + 3) / 3),
      S: date.getMilliseconds()
    }
    if (/(y+)/.test(fmt)) {
      fmt = fmt.replace(RegExp.$1, (date.getFullYear() + '').substr(4 - RegExp.$1.length))
    }
    for (const k in o) {
      if (new RegExp('(' + k + ')').test(fmt)) {
        fmt = fmt.replace(
          RegExp.$1,
          RegExp.$1.length === 1 ? o[k] : ('00' + o[k]).substr(('' + o[k]).length)
        )
      }
    }
    return fmt
  }
}

/**
 * 生成随机字符串
 */
export function toAnyString() {
  const str: string = 'xxxxx-xxxxx-4xxxx-yxxxx-xxxxx'.replace(/[xy]/g, (c: string) => {
    const r: number = (Math.random() * 16) | 0
    const v: number = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString()
  })
  return str
}

/**
 * 首字母大写
 */
export function firstUpperCase(str: string) {
  return str.toLowerCase().replace(/( |^)[a-z]/g, (L) => L.toUpperCase())
}

/**
 * 把对象转为formData
 */
export function objToFormData(obj: Recordable) {
  const formData = new FormData()
  Object.keys(obj).forEach((key) => {
    formData.append(key, obj[key])
  })
  return formData
}

/**
 * base64转File对象
 */
export const base64ToFile = (base64Data: string, filename: string): File => {
  // 去掉base64数据URL的前缀
  const arr = base64Data.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1] || ''
  const bstr = atob(arr[1])

  let n = bstr.length
  const u8arr = new Uint8Array(n)

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }

  return new File([u8arr], filename, { type: mime })
}

/**
 * 将秒数转换为易读的时间格式
 * @param seconds - 时间间隔（秒）
 * @returns 格式化后的时间字符串
 */
export const formatSeconds = (seconds: number): string => {
  if (seconds < 0) {
    return '0 s'
  }

  if (seconds < 60) {
    return `${seconds} s`
  }

  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const remainingSeconds = seconds % 60

  const parts: string[] = []

  if (days > 0) {
    parts.push(`${days} day `)
  }

  if (hours > 0) {
    parts.push(`${hours} hour `)
  }

  if (minutes > 0) {
    parts.push(`${minutes} min `)
  }

  if (remainingSeconds > 0) {
    parts.push(`${remainingSeconds} s`)
  }

  return parts.join(' ')
}

/** 格式化时间 */
export const formatDateTimerange = (
  dateTimerange: string[] | Date
): { start_time: string; end_time: string } => {
  if (dateTimerange instanceof Date) {
    return {
      start_time: formatTime(dateTimerange, 'yyyy-MM-dd 00:00:00') || '',
      end_time: formatTime(dateTimerange, 'yyyy-MM-dd 23:59:59') || ''
    }
  } else {
    return {
      start_time: formatTime(dateTimerange[0], 'yyyy-MM-dd HH:mm:ss') || '',
      end_time: formatTime(dateTimerange[1], 'yyyy-MM-dd HH:mm:ss') || ''
    }
  }
}

/**
 * @description 防抖函数-可配置立即执行和取消执行
 * @param func 需要防抖的函数
 * @param wait  延迟时间
 * @param immediate  是否立即执行
 * @returns debounced  返回一个函数 debounced.cancel() 可以取消执行
 */
export const debounce = (func: Function, wait: number = 500, immediate: boolean = false) => {
  let timeout: NodeJS.Timeout | null = null

  const debounced = function (this: any, ...args: any[]) {
    const callNow = immediate && !timeout

    if (timeout) {
      clearTimeout(timeout)
    }

    // 若配置了立即执行，则立即执行，否则在 wait 毫秒后执行
    if (callNow) {
      func.apply(this, args)
    }

    timeout = setTimeout(() => {
      timeout = null
      if (!immediate) {
        func.apply(this, args) // ✅ 执行即可
      }
    }, wait)
  }

  /**
   *  添加取消执行功能:
   *  清理定时器，防止延迟执行的函数在不恰当的时机运行
   */
  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout)
      timeout = null
    }
  }

  return debounced
}

export const shortcuts = [
  {
    text: '今天',
    value: () => {
      const end = new Date()
      const start = formatDateTimerange(new Date()).start_time
      end.setDate(end.getDate())
      return [start, end]
    }
  },
  {
    text: '上周',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 7)
      return [start, end]
    }
  },
  {
    text: '上个月',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setMonth(start.getMonth() - 1)
      return [start, end]
    }
  },
  {
    text: '最近三个月',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setMonth(start.getMonth() - 3)
      return [start, end]
    }
  }
]

/**
 * 单位转换工具函数（用于前后端数值比例转换）
 * @param value - 要转换的数值
 * @param options - 配置项
 * @param options.ratio - 转换比例，默认 100
 * @param options.decimals - 保留小数位数，默认 2
 * @param options.direction - 方向：toView=前端展示，toSubmit=提交后端 默认 toView
 * @returns 转换后的数值
 */
export const transformValue = (
  value: number,
  options: {
    ratio?: number
    decimals?: number
    direction?: TransformDirection
  } = {}
) => {
  const { ratio = 100, decimals = 2, direction = 'toView' } = options

  // 使用 BigNumber 精确运算，避免 IEEE 754 浮点误差
  // 例如 1142.6 * 100 原生计算得到 114259.99999999999，BigNumber 精确为 114260
  const bn = new BigNumber(value)
  const ratioBn = new BigNumber(ratio)
  const result = direction === 'toView' ? bn.dividedBy(ratioBn) : bn.multipliedBy(ratioBn)

  if (direction === 'toSubmit') {
    // BigNumber 乘除精确，直接转 number 返回
    return result.toNumber()
  }

  // toView：保留原逻辑，toFixed 控制展示精度
  return decimals !== null && decimals !== undefined ? +result.toFixed(decimals) : result.toNumber()
}

/**
 * 转换表单数据中的数字字段为数字类型
 * @param formData 表单数据对象
 * @param numbersKeys 数字字段数组
 */
export const transformNumber = (formData: Record<string, any>, numbersKeys: string[]) => {
  numbersKeys.forEach((key) => {
    if (formData[key]) {
      formData[key] = Number(formData[key])
    }
  })
}
